import { newArt } from '@textbox/core';
import { api, ApiError, type Asset } from '../api.ts';
import { ArtEditor } from '../art.ts';
import { confirmDialog, el, field, select, toast } from '../ui.ts';

const PRESETS: Array<{ label: string; width: number; height: number }> = [
  { label: 'Banner 40×4 (page title band)', width: 40, height: 4 },
  { label: 'Band 40×3', width: 40, height: 3 },
  { label: 'Strip 40×2', width: 40, height: 2 },
  { label: 'Element 20×4', width: 20, height: 4 },
  { label: 'Element 12×6', width: 12, height: 6 },
  { label: 'Full page 40×24', width: 40, height: 24 },
];

export async function assetsList(): Promise<HTMLElement> {
  const assets = await api.assets();
  const importName = el('input', { type: 'text', placeholder: 'Asset name' });
  const importFile = el('input', { type: 'file', accept: '.tti,text/plain' });
  const importRows = el('input', { type: 'text', value: '1-4', placeholder: '1-4', title: 'rows to take, e.g. 1-4 or 1-24' });
  const doImport = async () => {
    const f = importFile.files?.[0];
    if (!f || !importName.value.trim()) { toast('Choose a .tti file and give it a name', 'error'); return; }
    const buf = new Uint8Array(await f.arrayBuffer());
    const tti = Array.from(buf, (b) => String.fromCharCode(b)).join('');
    const m = /^(\d+)-(\d+)$/.exec(importRows.value.trim());
    try {
      const a = await api.importTti(importName.value.trim(), tti, m ? [Number(m[1]), Number(m[2])] : undefined);
      location.hash = `#/assets/${a.id}`;
    } catch (e) { toast(e instanceof ApiError ? e.message : 'import failed', 'error'); }
  };
  return el('section', {},
    el('div', { class: 'head' }, el('h1', {}, 'Page assets'), el('a', { class: 'button primary', href: '#/assets/new' }, 'New block art')),
    el('p', { class: 'muted' }, 'Block art for banners and page elements, drawn here or imported from a .tti made in edit.tf or the ZXNet editor.'),
    el('table', {}, el('thead', {}, el('tr', {}, ['Name', 'Kind', 'Size', 'Updated'].map((h) => el('th', {}, h)))),
      el('tbody', {}, assets.map((a: Asset) => el('tr', {},
        el('td', {}, el('a', { href: `#/assets/${a.id}` }, a.name)), el('td', {}, a.kind), el('td', {}, `${a.width}×${a.height}`), el('td', {}, new Date(a.updatedAt).toLocaleString()),
      )))),
    el('div', { class: 'import' }, el('h2', {}, 'Import .tti'), el('div', { class: 'frow' }, field('Name', importName), field('File', importFile), field('Rows', importRows)), el('button', { type: 'button', onClick: () => void doImport() }, 'Import')),
  );
}

export async function assetEditor(id: string | null): Promise<HTMLElement> {
  const existing = id ? await api.asset(id) : null;
  const name = el('input', { type: 'text', value: existing?.name ?? '', placeholder: 'Asset name' });
  const preset = select(PRESETS.map((p, i) => ({ value: String(i), label: p.label })), '0');
  const width = el('input', { type: 'number', value: String(existing?.width ?? 40), min: '1', max: '40' });
  const height = el('input', { type: 'number', value: String(existing?.height ?? 4), min: '1', max: '24' });
  const editor = new ArtEditor(existing ? ArtEditor.fromAsset(existing.rows, existing.width, existing.source) : { art: newArt(40, 4), texts: [] }, () => { /* live preview is drawn by the editor */ });

  const resize = () => {
    const w = Math.max(1, Math.min(40, Number(width.value)));
    const h = Math.max(1, Math.min(24, Number(height.value)));
    editor.resize(w, h);
  };
  preset.addEventListener('change', () => { const p = PRESETS[Number(preset.value)]!; width.value = String(p.width); height.value = String(p.height); resize(); });
  width.addEventListener('change', resize);
  height.addEventListener('change', resize);

  const save = async () => {
    if (!name.value.trim()) { toast('Give the asset a name', 'error'); return; }
    const body = { name: name.value.trim(), kind: 'art' as const, width: editor.source.art.width, height: editor.source.art.height, rows: editor.rows(), source: editor.source };
    try {
      const saved = existing ? await api.updateAsset(existing.id, body) : await api.createAsset(body);
      toast('Saved');
      if (!existing) location.hash = `#/assets/${saved.id}`;
    } catch (e) { toast(e instanceof ApiError ? e.message : 'save failed', 'error'); }
  };

  return el('section', { class: 'editor' },
    el('div', { class: 'head' }, el('h1', {}, existing ? `Asset: ${existing.name}` : 'New block art')),
    el('div', { class: 'frow' }, field('Name', name), field('Area preset', preset), field('Width (cells)', width), field('Height (rows)', height)),
    el('p', { class: 'muted' }, 'Left-drag paints sixels in the chosen colour; right-drag or Erase clears. Fill background paints the whole area. Add text places a pixel-font block you can drag, restyle and recolour until you bake it in; cut-out text costs nothing on a filled band. A cell holds one colour; cells lost to colour changes are outlined in magenta.'),
    editor.root,
    el('div', { class: 'actions' },
      el('button', { type: 'button', class: 'primary', onClick: () => void save() }, 'Save'),
      existing && el('button', { type: 'button', class: 'danger', onClick: async () => {
        if (!confirmDialog('Delete this asset?')) return;
        try { await api.deleteAsset(existing.id); location.hash = '#/assets'; } catch (e) { toast(e instanceof ApiError ? e.message : 'failed', 'error'); }
      } }, 'Delete'),
    ),
  );
}
